import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLang } from '@/i18n/LanguageContext';
import { API } from '@/lib/config';
import { Container, SectionKicker, TricolourRibbon } from '@/components/common/Layout';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Mail } from 'lucide-react';

export default function ContactPage() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success(res.data.message || t('contact.success'));
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.slice(-1)[0] || 'field'}: ${d.msg}`).join(', ')
        : (typeof detail === 'string' ? detail : 'Please check your inputs and try again.');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <section className="hero-surface">
        <Container className="py-14 sm:py-16">
          <SectionKicker>{t('nav.contact')}</SectionKicker>
          <h1 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0B1220]">
            {t('contact.title')}
          </h1>
          <p className="mt-4 max-w-3xl text-[#5B6B82] leading-relaxed">{t('contact.lede')}</p>
        </Container>
      </section>

      <section className="py-14">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8">
              <form onSubmit={onSubmit} data-testid="contact-form" className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field id="c-name" label={t('contact.form.name')}>
                  <Input id="c-name" required minLength={2} value={form.name} onChange={update('name')} data-testid="contact-name" />
                </Field>
                <Field id="c-email" label={t('contact.form.email')}>
                  <Input id="c-email" required type="email" value={form.email} onChange={update('email')} data-testid="contact-email" />
                </Field>
                <div className="md:col-span-2">
                  <Field id="c-subject" label={t('contact.form.subject')}>
                    <Input id="c-subject" required minLength={2} value={form.subject} onChange={update('subject')} data-testid="contact-subject" />
                  </Field>
                </div>
                <div className="md:col-span-2">
                  <Field id="c-message" label={t('contact.form.message')}>
                    <Textarea id="c-message" required minLength={5} rows={6} value={form.message} onChange={update('message')} data-testid="contact-message" />
                  </Field>
                </div>
                <div className="md:col-span-2">
                  <Button type="submit" disabled={submitting} className="bg-[#062B5C] text-white hover:bg-[#05244d]" data-testid="contact-form-submit-button">
                    {submitting ? t('common.submitting') : t('common.submit')}
                  </Button>
                </div>
              </form>
            </div>
            <aside className="lg:col-span-4">
              <Card className="border-[#D7E1EF] bg-white">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-[#F5F8FC] border border-[#D7E1EF] flex items-center justify-center text-[#062B5C]">
                      <Mail className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="font-display font-semibold text-[#0B1220]">Get in touch</div>
                      <div className="text-xs text-[#5B6B82]">We read every message.</div>
                    </div>
                  </div>
                  <div className="mt-5 text-sm text-[#0B1220] space-y-2">
                    <div>National Governance Reform Party</div>
                    <div className="text-[#5B6B82] text-xs">A modern civic-tech political movement for a stronger India.</div>
                  </div>
                </CardContent>
              </Card>
            </aside>
          </div>
        </Container>
      </section>
      <TricolourRibbon />
    </div>
  );
}

function Field({ id, label, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-sm font-medium text-[#0B1220]">{label} <span className="text-[#B42318]">*</span></Label>
      {children}
    </div>
  );
}
